import { useContext } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import { InquiryContext } from "components/store/source";
import { Header, Section, Text } from "components/ui/source";

import BaseInformation from "./BaseInformation";
import CollectionImage from "./CollectionImage";
import DefaultPrice from "./DefaultPrice";
import Features from "./Features";
import Quote from "./QuoteContainer";

import styles from "./Collection.module.scss";

function Collection({ className, collection, isDateSet }) {
  const { hasError } = useContext(InquiryContext);

  if (!collection) return null;

  const {
    adaAccessibilityInfo,
    amenities,
    available,
    collectionId,
    collectionName,
    bathroomCount,
    bedroomCount,
    longTermRental,
    minimumStayLength,
    quote,
    photos,
    sleepCount,
    squareFootage,
  } = collection;

  // Should show disabled state if date filter is set, but no quote available
  // Show disabled state on inquiry gql error
  const isUnavailable = (isDateSet && (!available || !quote)) || hasError;

  return (
    <Section className={classnames(styles.container, className)}>
      <CollectionImage
        className={classnames(
          styles.photo_container,
          isUnavailable && styles.disabled,
        )}
        collectionId={collectionId}
        photos={photos}
      />
      <div
        className={classnames(
          styles.description_container,
          isUnavailable && styles.disabled,
        )}
      >
        <Header className={styles.title} size="sm-md" type="headline4">
          {collectionName}
          {longTermRental && minimumStayLength && (
            <Text
              className={styles.min_los_label}
              element="span"
              types={["caption2"]}
              translateKey="listing:availability.collection.minimumStayLength"
              interpolation={{ minimumStayLength }}
            />
          )}
        </Header>

        <BaseInformation
          className={styles.base_information}
          bathroomCount={bathroomCount}
          bedroomCount={bedroomCount}
          sleepCount={sleepCount}
          squareFootage={squareFootage}
        />

        <Features
          className={styles.features}
          adaAccessibilityInfo={adaAccessibilityInfo}
          amenities={amenities}
        />
      </div>
      {isDateSet ? (
        <Quote className={styles.quote_container} collection={collection} />
      ) : (
        <DefaultPrice
          className={styles.quote_container}
          collection={collection}
        />
      )}
    </Section>
  );
}

Collection.propTypes = {
  className: PropTypes.string,
  collection: PropTypes.shape({
    adaAccessibilityInfo: PropTypes.shape({
      isHearingAccessible: PropTypes.bool.isRequired,
      isMobilityAccessible: PropTypes.bool.isRequired,
      mobilityFeatures: PropTypes.array,
      hearingFeatures: PropTypes.array,
    }),
    amenities: PropTypes.arrayOf(
      PropTypes.shape({
        active: PropTypes.bool,
        label: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    available: PropTypes.bool.isRequired,
    collectionId: PropTypes.string.isRequired,
    collectionName: PropTypes.string.isRequired,
    bathroomCount: PropTypes.number,
    bedroomCount: PropTypes.number,
    longTermRental: PropTypes.bool,
    minimumStayLength: PropTypes.number,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        caption: PropTypes.string,
        mediumSrc: PropTypes.string,
      }),
    ),
    quote: PropTypes.shape({
      totalLabel: PropTypes.string,
    }),
    sleepCount: PropTypes.number,
    squareFootage: PropTypes.number,
    viewCount: PropTypes.number,
  }),
  isDateSet: PropTypes.bool,
};

export default Collection;
